/**
 * Comment body — plain text (markdown-flavoured) with @-tokens.
 *
 * Storage: just a string. body in card_comments holds exactly what the
 * user typed. Tokens are parsed on the way IN (server-side) to find
 * mentions/dates, and parsed on the way OUT (client-side) for chip
 * rendering. There is no separate JSON column in use.
 *
 * Tokens recognised (case-insensitive on the @ tail):
 *   @name          → person mention; matched against a member list
 *                     (longest match wins so "@Alice Smith" beats "@Alice")
 *   @today / @tomorrow / @yesterday
 *   @monday … @sunday    (next occurrence; today if it IS today, else next)
 *   @YYYY-MM-DD     (literal ISO date)
 *
 * A name with whitespace is matched greedily; we don't require the user
 * to quote it. If a token doesn't resolve we leave it as plain text so
 * "@everyone" or "@email@host" doesn't get eaten.
 */

export type CommentNode =
  | { type: 'text'; text: string }
  | { type: 'mention'; userId: string; name: string }
  | { type: 'date'; iso: string; display: string }
  | { type: 'time'; minutes: number; display: string };

export type CommentBody = CommentNode[];

export interface MemberRef {
  id: string;
  name: string;
}

/**
 * Parse a comment body string against the given board members. Returns
 * a node list. Adjacent text nodes are merged.
 *
 * @param now Optional clock anchor — used to resolve relative date tokens
 *   like @today / @friday in tests.
 */
export function parseCommentBody(
  raw: string,
  members: MemberRef[],
  now: Date = new Date(),
): CommentBody {
  const out: CommentBody = [];
  // Build a name index, longest first so we match the most specific.
  const memberByName = [...members].sort((a, b) => b.name.length - a.name.length);

  let i = 0;
  while (i < raw.length) {
    const at = raw.indexOf('@', i);
    if (at === -1) {
      pushText(out, raw.slice(i));
      break;
    }
    // Anything before the @
    if (at > i) pushText(out, raw.slice(i, at));

    // An @ that's preceded by a non-whitespace, non-start char is probably
    // an email or list@thing — leave as text.
    if (at > 0 && !/[\s(]/.test(raw[at - 1] ?? '')) {
      pushText(out, '@');
      i = at + 1;
      continue;
    }

    // Try date tokens first (cheapest).
    const rest = raw.slice(at + 1);
    const dateMatch = tryParseDate(rest, now);
    if (dateMatch) {
      out.push({ type: 'date', iso: dateMatch.iso, display: dateMatch.display });
      i = at + 1 + dateMatch.consumed;
      continue;
    }
    // Then try @times: @9am / @10:30 / @14:00 / @2:15pm / @noon /
    // @midnight. Times never resolve to a date — they're a moment-in-
    // the-day, in the local 0-1439 minutes-from-midnight range.
    const timeMatch = tryParseTime(rest);
    if (timeMatch) {
      out.push({ type: 'time', minutes: timeMatch.minutes, display: timeMatch.display });
      i = at + 1 + timeMatch.consumed;
      continue;
    }

    // Try person mentions (longest member name that matches at this offset).
    let matched = false;
    for (const m of memberByName) {
      const lname = m.name.toLowerCase();
      if (rest.toLowerCase().startsWith(lname)) {
        // Must end at a word boundary (whitespace, punctuation, end of string).
        const nextChar = rest[lname.length] ?? '';
        if (nextChar === '' || /[\s.,;:!?)\]]/.test(nextChar)) {
          out.push({ type: 'mention', userId: m.id, name: m.name });
          i = at + 1 + lname.length;
          matched = true;
          break;
        }
      }
    }
    if (matched) continue;

    // No token matched — emit the @ as text and advance.
    pushText(out, '@');
    i = at + 1;
  }

  return out;
}

function pushText(out: CommentBody, text: string) {
  if (!text) return;
  const last = out[out.length - 1];
  if (last && last.type === 'text') last.text += text;
  else out.push({ type: 'text', text });
}

const RELATIVE_TOKENS = ['today', 'tomorrow', 'yesterday'] as const;
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

interface DateMatch {
  iso: string;
  display: string;
  /** Characters consumed from the input AFTER the '@'. */
  consumed: number;
}

function tryParseDate(rest: string, base: Date = new Date()): DateMatch | null {
  // ISO date first: YYYY-MM-DD
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(rest);
  if (iso && iso[1]) {
    const literal = iso[1];
    const d = new Date(literal + 'T00:00:00');
    if (!isNaN(+d)) return { iso: literal, display: shortDate(d), consumed: literal.length };
  }

  const today = startOfDay(base);
  const dayMs = 86_400_000;

  for (const t of RELATIVE_TOKENS) {
    if (matchesAtBoundary(rest, t)) {
      const delta = t === 'today' ? 0 : t === 'tomorrow' ? 1 : -1;
      const d = new Date(+today + delta * dayMs);
      return {
        iso: toIso(d),
        display: t.charAt(0).toUpperCase() + t.slice(1),
        consumed: t.length,
      };
    }
  }

  for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
    const name = DAY_NAMES[dayIdx];
    if (!name) continue;
    if (matchesAtBoundary(rest, name)) {
      let delta = (dayIdx - today.getDay() + 7) % 7;
      if (delta === 0) delta = 7;
      const d = new Date(+today + delta * dayMs);
      return {
        iso: toIso(d),
        display: name.charAt(0).toUpperCase() + name.slice(1),
        consumed: name.length,
      };
    }
  }
  return null;
}

function matchesAtBoundary(rest: string, token: string): boolean {
  if (rest.length < token.length) return false;
  if (rest.slice(0, token.length).toLowerCase() !== token) return false;
  const after = rest[token.length] ?? '';
  return after === '' || /[\s.,;:!?)\]]/.test(after);
}

interface TimeMatch {
  minutes: number;
  display: string;
  consumed: number;
}

/**
 * Parse time tokens: @noon, @midnight, @9am, @9pm, @10:30, @14:00,
 * @2:15pm. Returns minutes-from-midnight (0-1439).
 *
 * - 24-hour clock (HH:MM) is accepted strictly: hours 0-23, minutes 0-59.
 * - 12-hour clock requires am/pm and hours 1-12.
 * - Bare hour like @9am is accepted; @9 without am/pm is NOT (too
 *   ambiguous — could be a year, a draft count, anything).
 */
function tryParseTime(rest: string): TimeMatch | null {
  const lower = rest.toLowerCase();

  if (matchesAtBoundary(rest, 'noon')) {
    return { minutes: 12 * 60, display: 'Noon', consumed: 4 };
  }
  if (matchesAtBoundary(rest, 'midnight')) {
    return { minutes: 0, display: 'Midnight', consumed: 8 };
  }

  // HH:MM optionally followed by am/pm
  const colon = /^(\d{1,2}):(\d{2})(am|pm)?/.exec(lower);
  if (colon) {
    let h = Number(colon[1]);
    const m = Number(colon[2]);
    const period = colon[3];
    if (m > 59) return null;
    if (period) {
      if (h < 1 || h > 12) return null;
      if (period === 'am' && h === 12) h = 0;
      else if (period === 'pm' && h !== 12) h += 12;
    } else if (h > 23) return null;
    const consumed = colon[0].length;
    return {
      minutes: h * 60 + m,
      display: format12Hour(h, m),
      consumed,
    };
  }

  // Bare H or HH + am/pm (no colon, no minutes)
  const bare = /^(\d{1,2})(am|pm)/.exec(lower);
  if (bare) {
    let h = Number(bare[1]);
    const period = bare[2];
    if (h < 1 || h > 12) return null;
    if (period === 'am' && h === 12) h = 0;
    else if (period === 'pm' && h !== 12) h += 12;
    return {
      minutes: h * 60,
      display: format12Hour(h, 0),
      consumed: bare[0].length,
    };
  }

  return null;
}

function format12Hour(h: number, m: number): string {
  const period = h < 12 ? 'am' : 'pm';
  let hour = h % 12;
  if (hour === 0) hour = 12;
  if (m === 0) return `${hour}${period}`;
  return `${hour}:${m.toString().padStart(2, '0')}${period}`;
}

export function bodyToPlaintext(body: CommentBody): string {
  return body
    .map((n) => {
      if (n.type === 'text') return n.text;
      if (n.type === 'mention') return `@${n.name}`;
      if (n.type === 'date') return `@${n.display}`;
      if (n.type === 'time') return `@${n.display}`;
      return '';
    })
    .join('');
}

export function extractMentionedUserIds(body: CommentBody): string[] {
  const ids = new Set<string>();
  for (const n of body) if (n.type === 'mention') ids.add(n.userId);
  return [...ids];
}

export function extractDueDate(body: CommentBody): string | null {
  for (const n of body) if (n.type === 'date') return n.iso;
  return null;
}

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function shortDate(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Display string for a due date relative to "now": Today, Tomorrow, Fri,
 * May 20, Overdue · Mon.
 */
export function formatDueDate(iso: string, now: Date = new Date()): string {
  const target = new Date(iso + 'T00:00:00');
  const today = startOfDay(now);
  const dayMs = 86_400_000;
  const delta = Math.round((+startOfDay(target) - +today) / dayMs);
  if (delta === 0) return 'Today';
  if (delta === 1) return 'Tomorrow';
  if (delta === -1) return 'Yesterday';
  if (delta > 0 && delta < 7) return target.toLocaleDateString(undefined, { weekday: 'long' });
  if (delta < 0 && delta > -7)
    return `Overdue · ${target.toLocaleDateString(undefined, { weekday: 'short' })}`;
  return shortDate(target);
}
